import React, { FC, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useIntl, defineMessages } from "react-intl";
import { ResultCodes } from "@app/service/resultCodes";
import { useErrorController } from "@natera/platform/lib/hooks";
import { Progress, UpdateCommunicationForm, LinkHeap } from "@app/components";
import { Profile, ProfileContext } from "@app/provider/profile";
import { FactorContainerSignUp } from "@app/components/factorList";
import {
  MfaSetupContext,
  NotificationContext,
  OrderRenewalContext,
  UppAuthContext,
  UserContext,
} from "@app/provider";
import { routes } from "@app/routing";
import { STAGE } from "@app/service/user";
import ConfirmationForm from "./confirmationForm";
import { Communication } from "@app/provider/profile/types";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import acknowledgements from "@etc/acknowledgements.json";
import {
  TOKENIZED_SIGN_UP_TOTAL_STEPS,
  getSignUpFlowProgressValue,
} from "@app/utils/signUpFlowSteps";

const messages = defineMessages({
  confirmationStep: {
    id: "step",
    defaultMessage: "Step {number} of {count}",
  },
  confirmationTitle: {
    id: "confirmationTitle",
    defaultMessage: "Confirm Your Information",
  },
  confirmationTwoFactorTitle: {
    id: "confirmationTwoFactorTitle",
    defaultMessage: "Two-Factor Authentication",
  },
  confirmationUserAlreadyHaveLinkedProfileError: {
    id: "confirmationUserAlreadyHaveLinkedProfileError",
    defaultMessage:
      "Our records show that you already have an account with us. Please log in with the existing account.",
  },
  confirmationProfileAlreadyLinked: {
    id: "confirmationProfileAlreadyLinked",
    defaultMessage: "Profile already linked to another user",
  },
  confirmationMobileValidationError: {
    id: "confirmationMobileValidationError",
    defaultMessage: "You must enter a US based number to create an account.",
  },
  confirmationLogin: {
    id: "confirmationLogin",
    defaultMessage: "Log In",
  },
  confirmationWeAreSorry: {
    id: "confirmationWeAreSorry",
    defaultMessage:
      "We're sorry. An error occurred when setting up your profile.",
  },
  confirmationContactSupport: {
    id: "confirmationContactSupport",
    defaultMessage: "Contact support",
  },
});

const Confirmation: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    uppUser,
    loadProfile,
    updateUser,
    isLoading: isProfileLoading,
  } = useContext(UserContext);
  const { profile: uppProfile } = React.useContext(UppAuthContext);
  const { startRenewalWorkflow } = React.useContext(OrderRenewalContext);

  const [step, setStep] = useState(2);
  const errorController = useErrorController();

  const {
    confirmProfileIsLoading,
    confirmProfile,
    confirmProfileError,
    confirmProfileData,
    getProfileIsLoading,
    getPatientBasicInfo,
    getPatientBasicInfoData,
    profileData,
    skipCommunicationPreferencesStep,
    updatePatientCommunication,
    updatePatientCommunicationIsLoading,
  } = useContext(ProfileContext);
  const { addNotification } = useContext(NotificationContext);
  const { enrolledFactor } = useContext(MfaSetupContext);

  const [profile, setProfile] = useState<Profile | undefined>(undefined);
  const [phone, setPhone] = useState<string | undefined>(undefined);
  const isTokenizedFlow = Boolean(uppUser?.invite?.patientUID);

  useEffect(() => {
    loadProfile();
  }, []);

  // not sure if makes sense as an extra prevention (if user manually goes to /confirmation route), maybe should remove or place elsewhere? (same with setupProfile.tsx)
  useEffect(() => {
    if (uppUser && !uppUser.invite) {
      navigate(routes.setupProfile + location.search, { replace: true });
    }
  }, [uppUser?.invite, navigate, location.search]);

  useEffect(() => {
    if (uppProfile?.patientUID) {
      startRenewalWorkflow();
    }
  }, [uppProfile]);

  useEffect(() => {
    if (
      !isProfileLoading &&
      uppUser?.stage === STAGE.COMMUNICATION_PREFERENCES &&
      skipCommunicationPreferencesStep
    ) {
      updateUser({ stage: STAGE.FILLED });
    }
  }, [skipCommunicationPreferencesStep, isProfileLoading]);

  useEffect(() => {
    // todo remove DATE_OF_BIRTH_VERIFICATION and CONFIRMATION stage from db emums
    if (
      uppUser?.stage === STAGE.DATE_OF_BIRTH_VERIFICATION ||
      uppUser?.stage === STAGE.CONFIRMATION
    ) {
      updateUser({ stage: STAGE.BLANK });
      setStep(2);
    }
    if (uppUser?.stage === STAGE.MFA_SETUP) {
      setStep(3);
    } else if (uppUser?.stage === STAGE.COMMUNICATION_PREFERENCES) {
      setStep(4);
    } else if (uppUser?.stage === STAGE.FILLED) {
      navigate(routes.home + location.search, { replace: true });
    }
  }, [uppUser]);

  useEffect(() => {
    if (getPatientBasicInfoData) {
      setProfile(getPatientBasicInfoData);
    }
  }, [getPatientBasicInfoData]);

  useEffect(() => {
    if (isTokenizedFlow) {
      getPatientBasicInfo();
    }
  }, [uppUser]);

  useEffect(() => {
    if (confirmProfileError === ResultCodes.PHONE_VALIDATION_FAILED) {
      errorController.setValidationError(
        "mobileNumber",
        intl.formatMessage(messages.confirmationMobileValidationError)
      );
    }
    if (confirmProfileError === ResultCodes.PROFILE_ALREADY_LINKED_ERROR) {
      addNotification({
        type: "error",
        message: intl.formatMessage(
          messages.confirmationUserAlreadyHaveLinkedProfileError
        ),
        actions: (
          <Link to={routes.logOut}>
            {intl.formatMessage(messages.confirmationLogin)}
          </Link>
        ),
      });
    }
    if (confirmProfileError === ResultCodes.PROFILE_SETUP_ERROR) {
      addNotification({
        type: "error",
        message: intl.formatMessage(messages.confirmationWeAreSorry),
        actions: (
          <LinkHeap
            target="_blank"
            key="privacyPolicy"
            rel="noreferrer"
            to={acknowledgements.links.contactUs}
            heapEventName={HEAP_EVENTS.upp_click_contactnatera}
            heapEventProps={{
              location: HeapEventLocation.account_activated_error,
            }}
          >
            {intl.formatMessage(messages.confirmationContactSupport)}
          </LinkHeap>
        ),
      });
    }
  }, [confirmProfileError]);

  const handleConfirmationFormSubmit = async (profileData: Profile) => {
    await confirmProfile(profileData);
    loadProfile();
  };

  const handleCommunicationFormSubmit = async (
    communicationData: Communication[]
  ) => {
    await updatePatientCommunication(communicationData, {
      heapEventLocation: HeapEventLocation.accountCreation,
    });
    loadProfile();
  };

  const getTitle = () => {
    switch (step) {
      case 1:
      case 2: {
        return intl.formatMessage(messages.confirmationTitle);
      }
      case 3: {
        return intl.formatMessage(messages.confirmationTwoFactorTitle);
      }
    }
  };

  const getProgressValue = (step: number): number =>
    getSignUpFlowProgressValue(step, TOKENIZED_SIGN_UP_TOTAL_STEPS);

  return (
    <article className="setup-profile__container">
      {step !== 4 && (
        <Progress
          className="setup-profile__progress"
          value={getProgressValue(step)}
        />
      )}
      <div className="setup-profile__wrapper">
        <section className="setup-profile__content">
          {step !== 4 && (
            <h2 className="step-number">
              {intl.formatMessage(messages.confirmationStep, {
                number: step,
                count: TOKENIZED_SIGN_UP_TOTAL_STEPS,
              })}
            </h2>
          )}
          {!enrolledFactor && (
            <h3 className="setup-profile__title">{getTitle()}</h3>
          )}
          {step === 2 && profile && (
            <ConfirmationForm
              isLoading={confirmProfileIsLoading || getProfileIsLoading}
              profile={profile}
              onSubmit={handleConfirmationFormSubmit}
              clearErrors={errorController.clearErrors}
              clearValidationError={errorController.clearValidationError}
              setValidationError={errorController.setValidationError}
              getValidationError={errorController.getValidationError}
              setPhone={setPhone}
            />
          )}
          {step === 3 && uppUser?.askForMfa && (
            <FactorContainerSignUp
              isTokenizedFlow={isTokenizedFlow}
              profile={{
                ...confirmProfileData,
                email: uppUser.email ?? profile?.email,
                phone: confirmProfileData?.phone ?? phone ?? profile?.phone,
              }}
            />
          )}
          {step === 4 && !skipCommunicationPreferencesStep && (
            <UpdateCommunicationForm
              communicationData={profileData?.communicationPreferences}
              handleSubmit={handleCommunicationFormSubmit}
              className="communication-preferences"
              isLoading={updatePatientCommunicationIsLoading}
              isProfileSetup
            />
          )}
        </section>
      </div>
    </article>
  );
};

export default Confirmation;
